import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../ui/navigation-menu';
import { cn } from '../../lib/utils';
import { learnItems, toolsItems } from '../../lib/constants/navigationItems';

export function TopNavigation() {
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isLearnActive = learnItems.some(item => isActive(item.path));
  const isToolsActive = toolsItems.some(item => isActive(item.path));

  return (
    <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList>
        {/* Conversations */}
        <NavigationMenuItem>
          <Link to="/conversations">
            <NavigationMenuLink
              className={cn(
                navigationMenuTriggerStyle(),
                'text-base font-medium',
                isActive('/conversations')
                  ? 'bg-accent-base/20 text-accent-text'
                  : 'text-content hover:bg-accent-base/10 hover:text-accent-text'
              )}
            >
              {t('nav.conversations')}
            </NavigationMenuLink>
          </Link>
        </NavigationMenuItem>

        {/* Learn Dropdown */}
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className={cn(
              'text-base font-medium',
              isLearnActive
                ? 'bg-accent-base/20 text-accent-text'
                : 'text-content hover:bg-accent-base/10 hover:text-accent-text'
            )}
          >
            {t('nav.learn')}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
              {learnItems.map((item) => {
                const Icon = item.icon;
                return (
                  <ListItem
                    key={item.path}
                    href={item.path}
                    title={t(item.label)}
                    icon={<Icon className="h-4 w-4" />}
                  >
                    {item.description ? t(item.description) : ''}
                  </ListItem>
                );
              })}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Livestream */}
        <NavigationMenuItem>
          <Link to="/live-stream" className="relative">
            <NavigationMenuLink
              className={cn(
                navigationMenuTriggerStyle(),
                'text-base font-medium relative',
                isActive('/live-stream')
                  ? 'bg-accent-base/20 text-accent-text'
                  : 'text-content hover:bg-accent-base/10 hover:text-accent-text'
              )}
            >
              {t('nav.liveStream')}
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse-red"></span>
            </NavigationMenuLink>
          </Link>
        </NavigationMenuItem>

        {/* Tools Dropdown - only show if there are items */}
        {toolsItems.length > 0 && (
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className={cn(
                'text-base font-medium',
                isToolsActive
                  ? 'bg-accent-base/20 text-accent-text'
                  : 'text-content hover:bg-accent-base/10 hover:text-accent-text'
              )}
            >
              {t('nav.tools')}
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                {toolsItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <ListItem
                      key={item.path}
                      href={item.path}
                      title={t(item.label)}
                      icon={<Icon className="h-4 w-4" />}
                    >
                      {item.description ? t(item.description) : ''}
                    </ListItem>
                  );
                })}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'> & { icon?: React.ReactNode }
>(({ className, title, children, icon, href, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={href || '#'}
          ref={ref}
          className={cn(
            'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-2">
            {icon}
            <div className="text-sm font-medium leading-none">{title}</div>
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = 'ListItem';
